/**
 * CR Analysis API client — LISA backend routes for fetching/persisting CR
 * content (docs/flow/COMPASS_CR_IMPACT.md). The Compass call itself is in
 * compassApi.analyzeChange (browser → nginx → Compass directly).
 */
import { API_BASE, getAuthHeaders, fetchApi } from './apiClient';
import { CompassAnalysis } from './compassApi';
import { ImageExtractionResult } from './issueAnalysisApi';

export interface CrRecord {
  cr_key: string;
  summary: string | null;
  cr_text: string;
  char_count: number;
  attachments?: string[];
  extraction_results: ImageExtractionResult[];
  compass_response: CompassAnalysis | null;
  fetched_at: string;
  analyzed_at: string | null;
  /** Email of the analyst who ran the paid analysis. */
  analyzed_by?: string | null;
  found?: boolean;
}

/** One row of the CR list rail. `items`/`max_impact` drive the complexity
 *  rating, computed from the same numbers the page's summary card counts. */
export interface CrListItem {
  cr_key: string;
  summary: string | null;
  fetched_at: string;
  analyzed_at: string | null;
  items: number;
  max_impact: number;
  elapsed_ms: number;
  attachment_issues: number;
}

export interface CrListPage {
  items: CrListItem[];
  /** Rows matching the search before paging — drives the page controls. */
  total: number;
}

/** One page of stored CRs. Search is applied server-side, so it covers every
 *  stored CR rather than only the page currently loaded. */
export async function listCrs(opts?: {
  limit?: number;
  offset?: number;
  q?: string;
}): Promise<CrListPage> {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams();
  if (opts?.limit != null) params.set('limit', String(opts.limit));
  if (opts?.offset != null) params.set('offset', String(opts.offset));
  if (opts?.q) params.set('q', opts.q);
  const qs = params.toString();
  const response = await fetchApi(`${API_BASE}/cr-analysis${qs ? `?${qs}` : ''}`, {
    method: 'GET',
    headers,
  });
  if (!response.ok) throw new Error(`CR list failed (${response.status})`);
  const data = await response.json();
  return { items: data.items ?? [], total: data.total ?? 0 };
}

export async function deleteCr(crKey: string): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetchApi(`${API_BASE}/cr-analysis/${encodeURIComponent(crKey)}`, {
    method: 'DELETE',
    headers,
  });
  if (!response.ok) throw new Error(`CR delete failed (${response.status})`);
}

/** One JIRA attachment as listed by /cr-analysis/attachments — metadata
 *  only, nothing downloaded. */
export interface CrAttachment {
  id: string;
  filename: string;
  size: number | null;
  mime_type: string | null;
  created: string | null;
}

export interface CrAttachmentList {
  cr_key: string;
  summary: string;
  /** Server-enforced cap on how many attachments one run may extract. */
  max_selectable: number;
  attachments: CrAttachment[];
}

/** List the CR's attachment names (no content scanning) — feeds the
 *  selection grid shown before the real fetch. */
export async function listCrAttachments(crKey: string): Promise<CrAttachmentList> {
  const headers = await getAuthHeaders();
  const response = await fetchApi(`${API_BASE}/cr-analysis/attachments`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ cr_key: crKey }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new Error(data?.detail || `Attachment list failed (${response.status})`);
  }
  return response.json();
}

/** Pull the CR from JIRA (fresh), extract attachments, persist, return the
 *  full record including any previously stored Compass analysis.
 *  `selectedAttachmentIds` limits extraction to the picked attachments;
 *  omitted = all (legacy). */
export async function fetchCr(
  crKey: string,
  selectedAttachmentIds?: string[],
): Promise<CrRecord> {
  const headers = await getAuthHeaders();
  const response = await fetchApi(`${API_BASE}/cr-analysis/fetch`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      cr_key: crKey,
      selected_attachment_ids: selectedAttachmentIds,
    }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new Error(data?.detail || `CR fetch failed (${response.status})`);
  }
  return response.json();
}

/** Stored record without re-fetching JIRA (page restore). `found: false`
 *  when the CR was never fetched. */
export async function getCr(crKey: string): Promise<CrRecord> {
  const headers = await getAuthHeaders();
  const response = await fetchApi(
    `${API_BASE}/cr-analysis/${encodeURIComponent(crKey)}`,
    { method: 'GET', headers },
  );
  if (!response.ok) throw new Error(`CR load failed (${response.status})`);
  return response.json();
}

/** Persist the Compass /change/analyze response for a fetched CR. */
export async function saveCrCompass(
  crKey: string,
  analysis: CompassAnalysis,
): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetchApi(
    `${API_BASE}/cr-analysis/${encodeURIComponent(crKey)}/compass`,
    { method: 'PUT', headers, body: JSON.stringify(analysis) },
  );
  if (!response.ok) throw new Error(`CR analysis save failed (${response.status})`);
}
